import React, { useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Send } from "lucide-react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Block from "@/src/components/block/Block";
import DateTimePicker from "@/src/components/common/tools/DateTimePicker";
import { Combobox } from "@/src/components/form-fields/Combobox";
import DateTimeField from "@/src/components/form-fields/DateTimeField";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/src/components/form-fields/InputGroup";
import { TextField } from "@/src/components/form-fields/TextField";
import { Button } from "@/src/components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/src/components/ui/field";
import Breadcrumbs from "@/src/components/utils/Breadcrumbs";
import ContainerHeader from "@/src/components/utils/ContainerHeader";

import { getOfferOptions } from "./formParams/freecopiesForm";
import {
  buildFreecopyFormSchema,
  getFreecopyFormDefaultValues,
  isFreecopyActivated,
} from "./freecopyFormSchema";

import type { JSX } from "react";

import type {
  GetFreecopyResponse,
  OfferPlanListItem,
} from "@/src/api/OfferAPI.types";
import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";
import type { AppState } from "@/src/reducers";

import type { FreecopyFormValues } from "./freecopyFormSchema";

interface FreecopyFormProps {
  isEditMode: boolean;
  onSubmit: (values: FreecopyFormValues) => void;
  freecopy?: GetFreecopyResponse;
  offerPlans?: OfferPlanListItem[];
  isLoading: boolean;
  defaultLang: string;
}

function DaysDurationField(): JSX.Element {
  const { t } = useTranslation("editor-freecopy");
  const { errors } = useFormState<FreecopyFormValues>({
    name: "daysDuration",
  });
  const error = errors.daysDuration?.message;

  return (
    <Field>
      <FieldLabel>{t("freePeriod")}</FieldLabel>
      <InputGroup className="w-48" invalid={Boolean(error)}>
        <InputGroupInput<FreecopyFormValues>
          min={1}
          name="daysDuration"
          step={1}
          type="number"
        />
        <InputGroupAddon className="px-3 text-sm" position="end">
          {t("days")}
        </InputGroupAddon>
      </InputGroup>
      <FieldError>{error}</FieldError>
    </Field>
  );
}

function FreecopyForm({
  isEditMode,
  onSubmit,
  freecopy,
  offerPlans,
  isLoading,
  defaultLang,
}: FreecopyFormProps): JSX.Element {
  const { t } = useTranslation("editor-freecopy");
  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const isActivated = isFreecopyActivated(freecopy);
  const subscriptionBeginDate = freecopy?.subscription?.begin_date;
  const subscriptionBeginDateValue = subscriptionBeginDate
    ? new Date(subscriptionBeginDate)
    : undefined;

  const schema = useMemo(
    () => buildFreecopyFormSchema({ t, isActivated, subscriptionBeginDate }),
    [t, isActivated, subscriptionBeginDate],
  );

  const methods = useForm<FreecopyFormValues>({
    defaultValues: getFreecopyFormDefaultValues(freecopy, defaultLang),
    resolver: zodResolver(schema),
  });
  const submit = methods.handleSubmit(onSubmit);

  const languageLabels: Record<string, string> = t("languages:langs", {
    returnObjects: true,
  });
  const languageOptionList: ComboboxOption[] =
    project?.translations?.map((translation) => ({
      value: translation.language,
      label: languageLabels[translation.language] ?? translation.language,
    })) ?? [];
  const offerPlanOptionList: ComboboxOption[] =
    getOfferOptions({ t, offerPlans }) ?? [];

  const breadcrumbs = [
    { name: t("editor-menu:offerManagement"), url: "/freecopy" },
    { name: t("sectionTitleSimple"), url: "/freecopy" },
    { name: t(isEditMode ? "edit" : "new") },
  ];

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <div
        className="editor-container editor-has-breadcrumbs h-full p-6!"
        data-testid="FreecopyForm"
      >
        <Breadcrumbs items={breadcrumbs} />
        <ContainerHeader
          withBackBtn
          loading={isLoading}
          onSave={isEditMode ? submit : null}
          item={{
            ...freecopy,
            label: isEditMode ? t("editLabel") : t("newLabel"),
          }}
        />

        <Block>
          <FieldGroup className="max-w-xl">
            <TextField<FreecopyFormValues>
              disabled={isActivated}
              label={t("emailAddress")}
              name="email"
              placeholder={t("emailAddressesPlaceholder")}
            />
            {!isEditMode ? (
              <Combobox<FreecopyFormValues>
                emptyLabel={t("forms:noOptions")}
                label={t("chooseLanguage")}
                name="lang"
                options={languageOptionList}
                placeholder={t("forms:select")}
                searchPlaceholder={t("search")}
              />
            ) : null}
            <Combobox<FreecopyFormValues>
              emptyLabel={t("forms:noOptions")}
              label={t("offer")}
              name="offerPlanUuid"
              options={offerPlanOptionList}
              placeholder={t("selectOffer")}
              searchPlaceholder={t("search")}
            />
            {isActivated ? (
              <React.Fragment>
                <Field>
                  <FieldLabel>{t("subscriptionBegin")}</FieldLabel>
                  <DateTimePicker
                    disabled
                    onDateTimeChange={() => undefined}
                    value={subscriptionBeginDateValue}
                  />
                </Field>
                <DateTimeField<FreecopyFormValues>
                  label={t("subscriptionEnd")}
                  minDate={subscriptionBeginDateValue}
                  name="subscriptionEndDate"
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <DaysDurationField />
                <DateTimeField<FreecopyFormValues>
                  label={t("tokenExpiration")}
                  minDate={new Date()}
                  name="activationLimitDate"
                />
              </React.Fragment>
            )}
            {!isEditMode ? (
              <Button
                className="w-fit"
                disabled={isLoading}
                onClick={submit}
                type="button"
              >
                {t("emailsSend")}
                <Send />
              </Button>
            ) : null}
          </FieldGroup>
        </Block>
      </div>
    </FormProvider>
  );
}

export default FreecopyForm;
